import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, AlertTriangle, Shield, Lock, Zap, ArrowLeft } from "lucide-react";

const PrelimsTrapDetectorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mcqData, setMcqData] = useState({
    question: "",
    options: { A: "", B: "", C: "", D: "" },
    correct_answer: "A"
  });
  const [result, setResult] = useState(null);

  const isPro = user?.role === "PRO";

  const handleDetect = async () => {
    if (!isPro) {
      toast({
        variant: "destructive",
        description: "🔒 This feature is PRO-only. Upgrade to unlock Trap Detector."
      });
      navigate("/upgrade");
      return;
    }

    if (!mcqData.question.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a question"
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/trap/detect`,
        mcqData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: "Trap analysis complete!"
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to detect traps"
      });
    } finally {
      setLoading(false);
    }
  };

  const sampleMCQ = {
    question: "Consider the following statements about the Fundamental Rights in India:\n1. They are absolute and cannot be suspended under any circumstances.\n2. All Fundamental Rights are available against both State and private individuals.\n3. Article 32 is called the 'heart and soul' of the Constitution.\n\nWhich of the statements given above is/are correct?",
    options: {
      A: "1 and 2 only",
      B: "3 only",
      C: "1 and 3 only",
      D: "All of the above"
    },
    correct_answer: "B"
  };

  const loadSample = () => {
    setMcqData(sampleMCQ);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Trap Detector</h1>
          <p className="text-sm text-slate-600">
            Identify UPSC traps in MCQ options
          </p>
        </div>
        <Badge className={isPro ? "bg-amber-500" : "bg-red-500"}>
          {isPro ? "⭐ PRO" : "🔒 PRO ONLY"}
        </Badge>
      </div>

      {/* PRO-Only Lock Screen */}
      {!isPro && (
        <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-300">
          <CardContent className="p-12">
            <div className="text-center space-y-4">
              <Lock className="w-16 h-16 mx-auto text-orange-600" />
              <h2 className="text-2xl font-bold text-slate-900">
                PRO Feature Locked
              </h2>
              <p className="text-slate-700 max-w-lg mx-auto">
                Identify extreme words, absolute statements, NOT/ONLY traps, and over-precise statements. 
                Learn exactly how UPSC sets traps and how to avoid them!
              </p>
              <div className="pt-4">
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8">
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Interface (PRO) */}
      {isPro && (
        <>
          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Trap Types Detected:</p>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>• <span className="font-semibold">Extreme Words:</span> Always, never, all, none, completely</li>
                    <li>• <span className="font-semibold">Absolute Statements:</span> Only, solely, exclusively</li>
                    <li>• <span className="font-semibold">NOT/ONLY Traps:</span> Confusing negatives and restrictive words</li>
                    <li>• <span className="font-semibold">Over-Precise:</span> Exact dates, numbers that seem too specific</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900">Enter MCQ</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadSample}
                    disabled={loading}
                  >
                    Load Sample
                  </Button>
                </div>

                <div>
                  <Label htmlFor="question">Question</Label>
                  <Textarea
                    id="question"
                    placeholder="Enter the MCQ question"
                    value={mcqData.question}
                    onChange={(e) => setMcqData({ ...mcqData, question: e.target.value })}
                    disabled={loading}
                    rows={6}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Options</Label>
                  {Object.keys(mcqData.options).map((key) => (
                    <div key={key} className="flex gap-2">
                      <span className="font-semibold text-slate-700 min-w-[30px]">{key})</span>
                      <input
                        type="text"
                        value={mcqData.options[key]}
                        onChange={(e) =>
                          setMcqData({
                            ...mcqData,
                            options: { ...mcqData.options, [key]: e.target.value }
                          })
                        }
                        disabled={loading}
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                        placeholder={`Option ${key}`}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <Label htmlFor="correct_answer">Correct Answer</Label>
                  <select
                    id="correct_answer"
                    value={mcqData.correct_answer}
                    onChange={(e) => setMcqData({ ...mcqData, correct_answer: e.target.value })}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  >
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                  </select>
                </div>

                <Button
                  onClick={handleDetect}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Detecting Traps...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Detect Traps
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results Section */}
            <div className="space-y-4">
              {result && (
                <>
                  {/* Trap Type */}
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                        <h3 className="font-semibold text-slate-900">Trap Classification</h3>
                      </div>
                      <Badge className="bg-red-600 text-lg py-1 px-3">
                        {result.trap_type}
                      </Badge>
                    </CardContent>
                  </Card>

                  {/* Explanation */}
                  {result.explanation && (
                    <Card>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-slate-900 mb-3">
                          How UPSC Uses This Trap
                        </h3>
                        <p className="text-sm text-slate-700">
                          {result.explanation}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Avoidance Strategy */}
                  {result.avoidance_strategy && (
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-5 h-5 text-green-600" />
                          <h3 className="font-semibold text-slate-900">
                            How to Avoid This Trap
                          </h3>
                        </div>
                        <p className="text-sm text-slate-700">
                          {result.avoidance_strategy}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Option Analysis */}
                  {result.option_analysis && (
                    <Card>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-slate-900 mb-3">
                          Option-by-Option Analysis
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(result.option_analysis).map(([key, value]) => {
                            const isCorrect = key === mcqData.correct_answer;
                            return (
                              <div
                                key={key}
                                className={`p-3 rounded-lg border ${
                                  isCorrect
                                    ? "bg-green-50 border-green-300"
                                    : "bg-red-50 border-red-300"
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <Badge
                                    variant="outline"
                                    className={isCorrect ? "border-green-500" : "border-red-500"}
                                  >
                                    {key}
                                  </Badge>
                                  <div className="flex-1">
                                    <p className="text-sm text-slate-900 font-medium mb-1">
                                      {mcqData.options[key]}
                                    </p>
                                    <p className="text-xs text-slate-600">
                                      {value}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Placeholder */}
              {!result && !loading && (
                <Card className="border-dashed">
                  <CardContent className="p-12">
                    <div className="text-center text-slate-400">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Trap analysis will appear here</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PrelimsTrapDetectorPage;
